import { z } from "zod";
import { config } from "../../config/config";
import { authHeaders, handleError } from "./_helpers";
import type {
  AcceptLegalResponse,
  LegalAcceptanceList,
  LegalDocumentContent,
  LegalDocumentHistory,
  LegalDocumentType,
  PublishedLegalDocument,
} from "../../utils/zod/legal";
import {
  acceptLegalResponseSchema,
  legalAcceptanceListSchema,
  legalDocumentContentSchema,
  legalDocumentHistorySchema,
  publishedLegalDocumentSchema,
} from "../../utils/zod/legal";

const BASE_URL = `${config.API_URL}/v1/legal`;
const FORMAT_ERROR = "El documento legal llegó con un formato inesperado";

/** El diálogo ramifica sobre esto para volver a mostrar el texto vigente. */
export const STALE_LEGAL_VERSION_ERROR = "STALE_VERSION";

const parseOrThrow = <T>(schema: z.ZodType<T>, value: unknown): T => {
  const parsed = schema.safeParse(value);
  if (!parsed.success) throw new Error(FORMAT_ERROR);
  return parsed.data;
};

export const getLegalDocumentRequest = async (
  versionPublicId: string,
  token: string,
): Promise<LegalDocumentContent> => {
  const response = await fetch(`${BASE_URL}/documents/${versionPublicId}`, {
    method: "GET",
    headers: authHeaders(token),
  });
  if (!response.ok) {
    await handleError(response, "Error al obtener el documento legal");
  }
  return parseOrThrow(legalDocumentContentSchema, await response.json());
};

export const acceptLegalDocumentsRequest = async (
  documentVersionPublicIds: string[],
  token: string,
): Promise<AcceptLegalResponse> => {
  const response = await fetch(`${BASE_URL}/acceptances`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ documentVersionPublicIds }),
  });
  if (!response.ok) {
    if (response.status === 409) {
      const body = await response.json().catch(() => null);
      if ((body as { code?: string } | null)?.code === STALE_LEGAL_VERSION_ERROR) {
        throw new Error(STALE_LEGAL_VERSION_ERROR);
      }
    }
    await handleError(response, "Error al registrar la aceptación");
  }
  return parseOrThrow(acceptLegalResponseSchema, await response.json());
};

export const publishLegalDocumentRequest = async (
  input: { type: LegalDocumentType; content: string },
  token: string,
): Promise<PublishedLegalDocument> => {
  const response = await fetch(`${BASE_URL}/documents`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    await handleError(response, "Error al publicar el documento");
  }
  return parseOrThrow(publishedLegalDocumentSchema, await response.json());
};

export const getLegalDocumentHistoryRequest = async (
  params: { type: LegalDocumentType; limit: number; offset: number },
  token: string,
): Promise<LegalDocumentHistory> => {
  const query = new URLSearchParams({
    type: params.type,
    limit: String(params.limit),
    offset: String(params.offset),
  });
  const response = await fetch(`${BASE_URL}/documents?${query}`, {
    method: "GET",
    headers: authHeaders(token),
  });
  if (!response.ok) {
    await handleError(response, "Error al obtener el historial de versiones");
  }
  return parseOrThrow(legalDocumentHistorySchema, await response.json());
};

export interface LegalAcceptanceFilters {
  documentType?: LegalDocumentType | undefined;
  version?: number | undefined;
  gymPublicId?: string | undefined;
}

export const getLegalAcceptancesRequest = async (
  params: LegalAcceptanceFilters & { limit: number; offset: number },
  token: string,
): Promise<LegalAcceptanceList> => {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.documentType) query.set("documentType", params.documentType);
  if (params.version) query.set("version", String(params.version));
  if (params.gymPublicId) query.set("gymPublicId", params.gymPublicId);

  const response = await fetch(`${BASE_URL}/acceptances?${query}`, {
    method: "GET",
    headers: authHeaders(token),
  });
  if (!response.ok) {
    await handleError(response, "Error al obtener las aceptaciones");
  }
  return parseOrThrow(legalAcceptanceListSchema, await response.json());
};
